import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrip } from '@/context/TripContext';

const templates = [
  { id: 'checklist', name: '打卡清单', icon: 'ri-check-double-line' },
  { id: 'collage', name: '照片拼贴', icon: 'ri-gallery-line' },
  { id: 'family', name: '亲子回忆', icon: 'ri-parent-line' },
  { id: 'couple', name: '情侣旅行', icon: 'ri-heart-line' },
  { id: 'minimal', name: '极简路线', icon: 'ri-route-line' },
];

const mockPhotos = [
  'https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1533240332313-0db49b459ad6?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=200&h=200&fit=crop',
];

export default function SharePage() {
  const navigate = useNavigate();
  const { spotStatus, currentRoute } = useTrip();
  const [activeTemplate, setActiveTemplate] = useState('checklist');
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  // 没有 currentRoute 时显示空状态
  if (!currentRoute) {
    return (
      <div className="min-h-screen bg-bg pb-20">
        {toast && (
          <div className="fixed top-14 left-0 right-0 z-50 flex justify-center pointer-events-none">
            <div className="bg-text-primary/95 text-white text-sm px-5 py-2 rounded-full shadow-lg backdrop-blur-sm">
              {toast}
            </div>
          </div>
        )}
        <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <button
              className="w-8 h-8 flex items-center justify-center cursor-pointer"
              onClick={() => navigate(-1)}
            >
              <i className="ri-arrow-left-line text-lg text-text-body" />
            </button>
            <h1 className="text-lg font-bold text-text-primary">生成旅行记录</h1>
          </div>
        </header>
        <div className="flex flex-col items-center justify-center py-20 text-text-muted">
          <div className="w-20 h-20 rounded-full bg-bamboo-pale flex items-center justify-center mb-4">
            <i className="ri-image-line text-bamboo text-3xl" />
          </div>
          <p className="text-base font-medium text-text-primary">还没有可分享的行程</p>
          <p className="text-sm text-text-muted mt-1">完成打卡后生成你的安吉旅行记录</p>
          <button
            className="mt-6 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-bamboo cursor-pointer"
            onClick={() => navigate('/create-route')}
          >
            去生成路线
          </button>
        </div>
      </div>
    );
  }

  const route = currentRoute;

  // 只统计已完成的真实点位
  const allRealSpots = route.daysPlan
    .flatMap((d) => d.spots)
    .filter((s) => s.isRealPlace);

  const completedSpots = allRealSpots.filter(
    (s) => (spotStatus[s.id] || s.status) === 'completed',
  );

  const completedCount = completedSpots.length;
  const totalSpots = allRealSpots.length;
  const isAllCompleted = totalSpots > 0 && completedCount >= totalSpots;

  // 动态文案
  let shareTitle: string;
  let shareSubtitle: string;
  let quote: string;

  if (completedCount === 0) {
    shareTitle = '我的安吉之旅';
    shareSubtitle = '准备出发，生成同款路线';
    quote = '期待你的安吉故事';
  } else if (isAllCompleted) {
    shareTitle = `我在安吉玩了${route.days}天`;
    shareSubtitle = `完成 ${completedCount} 个地方`;
    quote = '这次最喜欢茶山和咖啡';
  } else {
    shareTitle = `我在安吉打卡了${completedCount}个地方`;
    shareSubtitle = '行程继续中';
    quote = '这趟旅程还在路上';
  }

  const visitedNames = completedSpots.map((s) => s.name);

  // 模板差异渲染
  const renderTemplateContent = () => {
    switch (activeTemplate) {
      case 'collage':
      case 'family':
      case 'couple':
        return (
          <>
            {completedCount > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {mockPhotos.slice(0, Math.min(4, Math.max(1, completedCount))).map((photo, i) => (
                  <img
                    key={i}
                    src={photo}
                    alt={`旅行照片 ${i + 1}`}
                    className="w-full h-28 object-cover rounded-lg"
                  />
                ))}
              </div>
            )}
            <p className="text-xs text-text-muted mt-3 text-center">
              {activeTemplate === 'family' && '记录一家人的美好时光'}
              {activeTemplate === 'couple' && '两个人的旅行记忆'}
              {activeTemplate === 'collage' && '用照片拼贴这段旅程'}
            </p>
          </>
        );
      case 'minimal':
        return (
          <div className="mt-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-bamboo-pale mb-2">
              <i className="ri-route-line text-bamboo text-2xl" />
            </div>
            <p className="text-3xl font-bold text-text-primary">
              {completedCount}<span className="text-lg text-text-muted">/{totalSpots}</span>
            </p>
            <p className="text-xs text-text-muted mt-1">已打卡 / 总地点</p>
          </div>
        );
      case 'checklist':
      default:
        return (
          <>
            {visitedNames.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {visitedNames.map((name, idx) => (
                  <div key={name} className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-bamboo text-sm" />
                    <span className="text-sm text-text-body">{name}</span>
                    <span className="text-[10px] text-text-muted ml-auto">第{idx + 1}站</span>
                  </div>
                ))}
                {totalSpots > completedCount && (
                  <div className="flex items-center gap-2 pt-1 border-t border-dashed border-divider">
                    <span className="text-xs text-text-muted">
                      还有 {totalSpots - completedCount} 个地点等待探索
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-bg pb-28">
      {/* Toast */}
      {toast && (
        <div className="fixed top-14 left-0 right-0 z-50 flex justify-center pointer-events-none">
          <div className="bg-text-primary/95 text-white text-sm px-5 py-2 rounded-full shadow-lg backdrop-blur-sm">
            {toast}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <button
            className="w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <i className="ri-arrow-left-line text-lg text-text-body" />
          </button>
          <h1 className="text-lg font-bold text-text-primary">生成旅行记录</h1>
        </div>
      </header>

      {/* Share Card Preview */}
      <div className="px-4 mt-3">
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          {/* Top Brand */}
          <div className="bg-bamboo px-4 py-3 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
              <i className="ri-map-pin-line text-white text-xs" />
            </div>
            <span className="text-white font-bold text-sm">玩安吉</span>
          </div>

          {/* Content */}
          <div className="p-4">
            <h2 className="text-xl font-bold text-text-primary">{shareTitle}</h2>
            <p className="text-sm text-bamboo font-medium mt-1">
              {completedCount > 0 ? shareSubtitle : '开始我的安吉之旅'}
            </p>

            {/* Template-specific content */}
            {renderTemplateContent()}

            {/* Photos fallback for checklist when no template-specific photos */}
            {activeTemplate === 'checklist' && completedCount > 0 && (
              <div className="mt-4 grid grid-cols-4 gap-1.5">
                {mockPhotos.slice(0, Math.min(4, completedCount)).map((photo, i) => (
                  <img
                    key={i}
                    src={photo}
                    alt={`旅行照片 ${i + 1}`}
                    className="w-full h-16 object-cover rounded-lg"
                  />
                ))}
              </div>
            )}

            {/* Quote */}
            <div className="mt-4 bg-bamboo-pale/40 rounded-xl p-3 text-center">
              <i className="ri-double-quotes-l text-bamboo-light text-lg" />
              <p className="text-sm text-text-body mt-1">{quote}</p>
              <p className="text-xs text-text-muted mt-1">— 2026.05.26</p>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-divider flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-bamboo-pale flex items-center justify-center">
                  <i className="ri-qr-code-line text-bamboo text-lg" />
                </div>
                <div>
                  <p className="text-xs font-bold text-text-primary">扫码生成同款路线</p>
                  <p className="text-[10px] text-text-muted">玩安吉 · 安吉旅行助手</p>
                </div>
              </div>
              <span className="text-[10px] text-bamboo font-medium">#安吉旅行</span>
            </div>
          </div>
        </div>
      </div>

      {/* Templates —— 两行布局 */}
      <div className="px-4 mt-5">
        <h3 className="text-sm font-bold text-text-primary mb-2">选择模板</h3>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {templates.slice(0, 3).map((tmpl) => (
            <button
              key={tmpl.id}
              onClick={() => setActiveTemplate(tmpl.id)}
              className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl cursor-pointer border transition-colors ${
                activeTemplate === tmpl.id
                  ? 'bg-bamboo-pale border-bamboo text-bamboo'
                  : 'bg-white border-divider text-text-body'
              }`}
            >
              <i className={`${tmpl.icon} text-lg`} />
              <span className="text-[10px] font-medium">{tmpl.name}</span>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {templates.slice(3).map((tmpl) => (
            <button
              key={tmpl.id}
              onClick={() => setActiveTemplate(tmpl.id)}
              className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl cursor-pointer border transition-colors ${
                activeTemplate === tmpl.id
                  ? 'bg-bamboo-pale border-bamboo text-bamboo'
                  : 'bg-white border-divider text-text-body'
              }`}
            >
              <i className={`${tmpl.icon} text-lg`} />
              <span className="text-[10px] font-medium">{tmpl.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-divider px-4 py-3">
        <div className="flex gap-3 max-w-lg mx-auto">
          <button
            className="flex-1 py-2.5 rounded-lg border border-divider text-sm font-medium text-text-body cursor-pointer bg-white flex items-center justify-center gap-1.5"
            onClick={() => showToast('图片已保存')}
          >
            <i className="ri-download-line" />
            保存图片
          </button>
          <button
            className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white bg-bamboo cursor-pointer flex items-center justify-center gap-1.5"
            onClick={() => showToast('分享成功')}
          >
            <i className="ri-share-line" />
            分享给朋友
          </button>
        </div>
      </div>
    </div>
  );
}